/**
 * Connect-vyn hanterar inloggning och datahämtning, sen lämnar den
 * över till Simulator-vyn via onDataLoaded.
 */

import { useEffect, useState } from "react";
import {
  getSession,
  getHomes,
  getData,
  logout,
  startTibberAuth,
  ApiError,
} from "../api/client";
import type { TibberHome, UserData } from "../types";
import { BatteryLoader } from "../components/BatteryLoader";
import CsvUpload from "../components/CsvUpload";
import Wizard from "../components/Wizard";

type Step =
  | "checking-session"
  | "needs-connect"
  | "csv-upload"
  | "wizard"
  | "select-home"
  | "loading-data";

function readAuthErrorFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  const err = params.get("auth_error");
  if (err) {
    const url = new URL(window.location.href);
    url.searchParams.delete("auth_error");
    window.history.replaceState({}, "", url.toString());
  }
  return err;
}

interface ConnectProps {
  onDataLoaded: (data: UserData) => void;
}

export default function Connect({ onDataLoaded }: ConnectProps) {
  const [step, setStep] = useState<Step>("checking-session");
  const [homes, setHomes] = useState<TibberHome[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const authError = readAuthErrorFromUrl();
    if (authError) setError(translateAuthError(authError));

    getSession()
      .then(async (s) => {
        if (s.authenticated && !s.expired) {
          try {
            const { homes } = await getHomes();
            setHomes(homes);
            if (homes.length === 1 && homes[0]) {
              await loadHomeData(homes[0].id);
            } else {
              setStep("select-home");
            }
          } catch (err) {
            handleError(err);
            setStep("needs-connect");
          }
        } else {
          setStep("needs-connect");
        }
      })
      .catch(() => setStep("needs-connect"));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleError(err: unknown) {
    setError(err instanceof Error ? err.message : String(err));
  }

  async function loadHomeData(homeId: string) {
    setError(null);
    setBusy(true);
    setStep("loading-data");
    try {
      const data = await getData(homeId, 8760);
      onDataLoaded(data);
    } catch (err) {
      handleError(err);
      if (err instanceof ApiError && err.status === 401) {
        setStep("needs-connect");
      } else {
        setStep("select-home");
      }
      setBusy(false);
    }
  }

  async function handleLogout() {
    await logout().catch(() => null);
    setHomes([]);
    setStep("needs-connect");
  }

  if (step === "checking-session") {
    return (
      <section className="connect">
        <p className="hint">Kollar inloggning…</p>
      </section>
    );
  }

  if (step === "needs-connect") {
    return (
      <section className="connect connect-landing">
        <div className="data-source-options">
          <button
            type="button"
            className="data-source-card"
            onClick={() => setStep("wizard")}
          >
            <strong>Mata in din profil</strong>
            <span>
              Svara på fem frågor om ditt hem. Fungerar för alla, oavsett
              elavtal.
            </span>
          </button>

          <button
            type="button"
            className="data-source-card data-source-card-accent"
            onClick={() => startTibberAuth(window.location.origin + "/")}
          >
            <strong>Hämta från Tibber</strong>
            <span>
              Bästa precisionen om du har Tibber-konto. Vi läser din senaste
              års timdata via deras OAuth.
            </span>
          </button>
        </div>

        {error && <p className="error">{error}</p>}
      </section>
    );
  }

  if (step === "csv-upload") {
    return (
      <CsvUpload
        onDataLoaded={onDataLoaded}
        onCancel={() => setStep("needs-connect")}
      />
    );
  }

  if (step === "wizard") {
    return (
      <Wizard
        onDataLoaded={onDataLoaded}
        onCancel={() => setStep("needs-connect")}
      />
    );
  }

  if (step === "select-home") {
    return (
      <section className="connect">
        <h2>Välj hus</h2>
        <ul className="homes">
          {homes.map((home) => (
            <li key={home.id}>
              <button
                type="button"
                onClick={() => loadHomeData(home.id)}
                disabled={busy}
              >
                <strong>{home.nickname ?? "Hus utan namn"}</strong>
                <span>
                  {home.address.address1 ?? "okänd adress"}
                  {home.priceArea && ` · ${home.priceArea}`}
                </span>
              </button>
            </li>
          ))}
        </ul>
        {error && (
          <div className="connect-error">
            <p className="error">{error}</p>
            <p className="hint">
              Klicka på huset igen för att försöka på nytt.
            </p>
          </div>
        )}
        <button type="button" className="link" onClick={handleLogout}>
          Logga ut
        </button>
      </section>
    );
  }

  if (step === "loading-data") {
    return (
      <section className="connect connect-loading">
        <BatteryLoader size={120} label="Hämtar ett år av data" />
        <p className="hint">
          Drar timdata från Tibber och spotpriser från elprisetjustnu.se.
          Det är 8760 timmar att gå igenom så det tar några sekunder.
        </p>
      </section>
    );
  }

  return null;
}

function translateAuthError(code: string): string {
  switch (code) {
    case "missing_params":
      return "Tibber-inloggningen avbröts på vägen tillbaka. Försök igen.";
    case "state_mismatch":
      return "Säkerhetskontrollen misslyckades. Försök igen.";
    case "token_exchange_failed":
      return "Kunde inte koppla din Tibber-anslutning. Försök igen om en stund.";
    case "access_denied":
      return "Du behöver godkänna åtkomsten i Tibber-dialogen för att appen ska kunna räkna på din data.";
    default:
      return "Tibber-inloggningen misslyckades. Försök igen.";
  }
}
